import db from "@/plugins/dexie";
import apirecebimentoweb from '@/services/api-recebimentoweb';
import moment from "moment-timezone";
import geraData from "../geradata";
import { formulario, formularioidentificacao, formularioresposta } from "./tabelas";

// Envia a fila formularioenvio para POST formularioresposta/sincronizar.
// Mesmo padrão do enviaApontamento, mas nada é descartado (conflito/falha ficam no banco).

// Lock de envio abandonado expira em 1 min.
const TTL_LOCK_MINUTOS = 1;

// Backoff: 30s, 1min, 2min... até 15min.
const BACKOFF_BASE_SEGUNDOS = 30;
const BACKOFF_TETO_SEGUNDOS = 15 * 60;

function isLockExpirado(item, minutos = TTL_LOCK_MINUTOS) {
  if (!item.fendataprocesso) return false;

  return moment(geraData()).diff(moment(item.fendataprocesso), 'minutes') > minutos;
}

function proximaTentativa(tentativas) {
  const segundos = Math.min(BACKOFF_BASE_SEGUNDOS * (2 ** tentativas), BACKOFF_TETO_SEGUNDOS);

  return moment(geraData()).add(segundos, 'seconds').toISOString();
}

function blobParaBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

// Anexos voltam a ser { data: base64, nome, tipo, idform }.
async function montaRespostas(envio) {
  const anexos = await db.formularioenvioarquivo.where({ faeidenvio: envio.id }).toArray();
  const porIndice = anexos.reduce((mapa, anexo) => ({ ...mapa, [anexo.faeindice]: anexo }), {});

  const respostas = [];
  // eslint-disable-next-line no-restricted-syntax
  for (const [indice, item] of envio.fenpayload.respostas.entries()) {
    const anexo = item.anexo != null ? porIndice[item.anexo] : null;

    if (anexo) {
      // eslint-disable-next-line no-await-in-loop
      const base64 = await blobParaBase64(anexo.faeblob);
      respostas.push({
        questao: item.questao,
        resposta: {
          data: base64,
          nome: anexo.faenome,
          tipo: anexo.faetipo,
          idform: envio.fenidformulario,
        },
      });
    } else {
      respostas.push({ questao: item.questao, resposta: item.resposta ?? null, indice });
    }
  }

  return respostas.map(({ questao, resposta }) => ({ questao, resposta }));
}

export default async function $_enviaFormulario() {
  const tokenRaw = sessionStorage.getItem('auth:token');
  if (!tokenRaw) return;

  const token = JSON.parse(tokenRaw);
  const agora = geraData();

  // Envios já confirmados pelo fluxo anterior (marcados com fendatanuvem) não têm mais uso.
  const confirmados = await db.formularioenvio.filter((envio) => !!envio.fendatanuvem).primaryKeys();
  if (confirmados.length) {
    await db.formularioenvioarquivo.where('faeidenvio').anyOf(confirmados).delete();
    await db.formularioenvio.bulkDelete(confirmados);
  }

  const todos = await db.formularioenvio.toArray();
  const pendentes = todos
    .filter((envio) => !envio.fendatanuvem
      && !envio.fenfalhapermanente
      && !envio.fenconflito
      && (!envio.fenproximatentativa || new Date(envio.fenproximatentativa) <= new Date(agora)))
    // Ordem de gravação (rascunho antes da conclusão).
    .sort((a, b) => new Date(a.fendatagravacao) - new Date(b.fendatagravacao));

  // eslint-disable-next-line no-restricted-syntax
  for (const pendente of pendentes) {
    let item;

    // Lock por registro.
    // eslint-disable-next-line no-await-in-loop
    const podeProcessar = await db.transaction('rw', db.formularioenvio, async () => {
      const atual = await db.formularioenvio.get(pendente.id);
      if (!atual) return false;
      if (atual.fendatanuvem) return false;
      if (atual.fenemprocesso && !isLockExpirado(atual)) return false;

      await db.formularioenvio.update(atual.id, {
        fenemprocesso: true,
        fendataprocesso: geraData(),
      });

      item = { ...atual };
      return true;
    });

    if (!podeProcessar || !item) continue;

    let respostas;
    try {
      // eslint-disable-next-line no-await-in-loop
      respostas = await montaRespostas(item);
    } catch (err) {
      console.error('[ENVIO_CHECKLIST] falha ao montar payload', item.id, err);
      // eslint-disable-next-line no-await-in-loop
      await db.formularioenvio.update(item.id, {
        fenemprocesso: false,
        fendataprocesso: null,
        fenfalhapermanente: true,
        fenerro: 'Não foi possível ler os anexos gravados no dispositivo',
      });
      continue;
    }

    const payload = {
      formulario: item.fenidformulario,
      chaveenvio: item.fenchaveenvio,
      user: JSON.stringify(item.fenpayload.user ?? {}),
      useidusuario: item.fenpayload.useidusuario ?? null,
      concluir: !!item.fenpayload.concluir,
      respostas,
    };

    let res;
    try {
      // eslint-disable-next-line no-await-in-loop
      res = await apirecebimentoweb.post('/formularioresposta/sincronizar', payload, {
        headers: { Authorization: `Bearer ${token.token}` },
      });
    } catch (err) {
      const status = err?.response?.status;
      console.error('[ENVIO_CHECKLIST_ERRO]', item.id, status, err);

      // Já finalizado no servidor.
      if (status === 409) {
        // eslint-disable-next-line no-await-in-loop
        await db.formularioenvio.update(item.id, {
          fenemprocesso: false,
          fendataprocesso: null,
          fenconflito: true,
          fenerro: err?.response?.data?.message ?? 'Formulário já finalizado no servidor',
        });
        continue;
      }

      // Recusa definitiva.
      if (status === 422 || status === 413) {
        // eslint-disable-next-line no-await-in-loop
        await db.formularioenvio.update(item.id, {
          fenemprocesso: false,
          fendataprocesso: null,
          fenfalhapermanente: true,
          fenerro: err?.response?.data?.message ?? 'Envio recusado pelo servidor',
        });
        continue;
      }

      // Sessão expirada: libera sem contar tentativa.
      if (status === 401 || status === 403) {
        // eslint-disable-next-line no-await-in-loop
        await db.formularioenvio.update(item.id, {
          fenemprocesso: false,
          fendataprocesso: null,
        });
        return;
      }

      // Rede/servidor: agenda nova tentativa.
      const tentativas = (item.fententativas ?? 0) + 1;
      // eslint-disable-next-line no-await-in-loop
      await db.formularioenvio.update(item.id, {
        fenemprocesso: false,
        fendataprocesso: null,
        fententativas: tentativas,
        fenproximatentativa: proximaTentativa(tentativas),
        fenerro: err?.message ?? 'Falha de conexão',
      });
      continue;
    }

    // Sucesso: grava o retorno nos espelhos (como o apontamento) e tira da fila.
    // eslint-disable-next-line no-await-in-loop
    await $_gravaRetorno(res?.data ?? {});
    // eslint-disable-next-line no-await-in-loop
    await db.transaction('rw', db.formularioenvio, db.formularioenvioarquivo, async () => {
      await db.formularioenvioarquivo.where({ faeidenvio: item.id }).delete();
      await db.formularioenvio.delete(item.id);
    });
    console.log('[ENVIO_CHECKLIST] enviado', item.fenidformulario, res?.data?.jaAplicado ? '(já aplicado)' : '');
  }
}

async function $_upsert(tabela, campoIdnuvem, registro) {
  const atual = await db[tabela].where({ [campoIdnuvem]: registro[campoIdnuvem] }).first();
  if (atual) return db[tabela].update(atual.id, registro);
  return db[tabela].add(registro);
}

// Espelhos passam a refletir o servidor na hora; o delta depois só atualiza pelo idnuvem.
async function $_gravaRetorno(retorno) {
  const tabelas = [db.formulario, db.formularioidentificacao, db.formularioresposta];

  await db.transaction('rw', tabelas, async () => {
    if (retorno.formulario) {
      await $_upsert('formulario', 'foridnuvem', formulario(retorno.formulario));
    }

    if (!retorno.formularioidentificacao) return;

    const ident = formularioidentificacao(retorno.formularioidentificacao);
    await $_upsert('formularioidentificacao', 'foiidnuvem', ident);

    // Respostas da identificação são substituídas pelas do servidor.
    await db.formularioresposta.where({ frsidformularioidentificacao: ident.foiidnuvem }).delete();
    await db.formularioresposta.bulkAdd((retorno.formularioresposta ?? []).map(formularioresposta));
  });
}
